import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import RichTextEditor, { PREVIEW_BREAK_RE } from "../components/RichTextEditor";
import CoverImageDropzone from "../components/CoverImageDropzone";
import { ArrowLeft, Eye, Save, CheckCircle2, AlertCircle, X } from "lucide-react";
import CourseLinksPicker from "../components/CourseLinksPicker";
import { COURSE_TYPES, COURSE_LEVELS } from "../lib/courseMeta";
import GlossaryPanel from "../components/GlossaryPanel";
import TocPanel from "../components/editor/TocPanel";
import { DraftBanner, DraftStatus } from "../components/editor/DraftNotice";
import useDraft from "../lib/useDraft";
import { useInternalLinks } from "../lib/internalLinks";
import { confirmDialog } from "../components/ConfirmDialog";
import { AuthorPicker, RetroDateFields } from "../components/editor/EditorFields";

const EMPTY = {
  title: "",
  description: "",
  category: "article",
  content: "",
  thumbnail: "",
  youtube_url: "",
  is_premium: false,
  published: true,
  tags: [],
  level: "debutant",
  year_start: null, // rétrospectives : année (négative = av. J.-C.)
  year_end: null, // année de fin, facultative
  author_ids: [], // vide = l'admin connecté
  links: [],
  glossary_off: [],
  theme_id: "",
  toc: null,
};

function fromCourse(c) {
  return {
    title: c.title || "",
    description: c.description || "",
    category: c.category || "article",
    content: c.content || "",
    thumbnail: c.thumbnail || "",
    youtube_url: c.youtube_url || "",
    is_premium: !!c.is_premium,
    published: c.published !== false,
    tags: c.tags || [],
    level: c.level || "",
    year_start: typeof c.year_start === "number" ? c.year_start : null,
    year_end: typeof c.year_end === "number" ? c.year_end : null,
    author_ids: c.author_ids || [],
    links: c.links || [],
    glossary_off: c.glossary_off || [],
    theme_id: c.theme_id || "",
    toc: c.toc || null,
  };
}

const CONTENT_TYPES = COURSE_TYPES;
const LEVEL_OPTIONS = COURSE_LEVELS.map((l) => ({ id: l.id, label: l.label }));

function Toggle2({ options, value, onChange, testid }) {
  return (
    <div className="inline-flex flex-wrap items-center gap-1 border border-white/10 rounded-lg p-1 bg-white/[0.02]" data-testid={testid}>
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => onChange(o.id)}
          data-testid={`${testid}-${o.id}`}
          className={`rounded-md px-3 py-1.5 text-xs uppercase tracking-[0.12em] transition-colors ${
            value === o.id ? "bg-emerald-500 text-black" : "text-zinc-400 hover:text-zinc-200"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
export default function AdminCourseEditorPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const isEdit = !!id;
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [form, setForm] = useState(EMPTY);
  const [dirty, setDirty] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const loadedId = useRef(null);

  const { data: course, isLoading } = useQuery({
    queryKey: ["admin-course", id],
    queryFn: async () => (await api.get(`/courses/${id}`)).data,
    enabled: isEdit,
  });

  const slugForView = course?.slug;
  // Thématiques (pour les concepts) et définitions du glossaire (bouton « Glossaire » et mots détectés)
  const { data: themes = [] } = useQuery({
    queryKey: ["course-themes"],
    queryFn: async () => (await api.get("/course-themes")).data,
  });
  const { data: allCourses = [] } = useQuery({
    queryKey: ["admin-courses"],
    queryFn: async () => (await api.get("/courses")).data,
  });
  const glossaryTerms = useMemo(
    () => allCourses.filter((c) => c.category === "vocabulary" && c.published !== false && c.id !== id),
    [allCourses, id]
  );
  useEffect(() => {
    if (isEdit && course && loadedId.current !== id) {
      loadedId.current = id;
      setForm(fromCourse(course));
      setDirty(false);
    }
  }, [isEdit, course, id]);

  const update = useCallback((patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
    setDirty(true);
  }, []);

  // Brouillon automatique (sur cet appareil) et liens internes
  const draft = useDraft(`draft:course:${id || "new"}`, form, { ready: !isEdit || (!!course && loadedId.current === id), dirty });
  const { targets: linkTargets } = useInternalLinks();
  const internalTargets = useMemo(() => linkTargets.filter((t) => t.key !== `course:${id}`), [linkTargets, id]);

  const createMut = useMutation({
    mutationFn: async (payload) => (await api.post("/courses", payload)).data,
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ["admin-courses"] });
      qc.invalidateQueries({ queryKey: ["courses"] });
      qc.invalidateQueries({ queryKey: ["course-themes"] });
      setDirty(false);
      draft.clear();
      loadedId.current = created.id;
      toast.success("Cours créé");
      navigate("/admin/cours");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });

  const updateMut = useMutation({
    mutationFn: async (payload) => (await api.patch(`/courses/${id}`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-courses"] });
      qc.invalidateQueries({ queryKey: ["courses"] });
      qc.invalidateQueries({ queryKey: ["admin-course", id] });
      qc.invalidateQueries({ queryKey: ["course-themes"] });
      setDirty(false);
      draft.clear();
      toast.success("Cours mis à jour");
      navigate("/admin/cours");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });

  const saving = createMut.isPending || updateMut.isPending;

  const save = useCallback(() => {
    if (saving) return;
    if (!form.title.trim() || !form.content.trim()) {
      toast.error("Titre et contenu requis");
      return;
    }
    // Un niveau vide (anciens cours) n'est pas envoyé ; date : 0 = pas de date (ou date retirée)
    const { period, ...rest } = form; // eslint-disable-line no-unused-vars
    const payload = {
      ...rest,
      level: form.level || null,
      year_start: form.category === "article" ? form.year_start || 0 : 0,
      year_end: form.category === "article" ? form.year_end || 0 : 0,
      author_ids: form.author_ids?.length ? form.author_ids : undefined,
    };
    if (isEdit) updateMut.mutate(payload);
    else createMut.mutate(payload);
  }, [saving, form, isEdit, updateMut, createMut]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "s" || e.key === "S")) {
        e.preventDefault();
        save();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [save]);

  useEffect(() => {
    if (!dirty) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const goBack = async () => {
    if (dirty && !(await confirmDialog({ title: "Quitter sans enregistrer ?", description: "Vos dernières modifications ne sont pas enregistrées. Le brouillon reste sur cet appareil.", confirmLabel: "Quitter", tone: "warning" }))) return;
    navigate("/admin/cours");
  };

  const viewCourse = () => {
    if (slugForView) window.open(`/cours/${slugForView}`, "_blank");
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    if (!form.tags.includes(t)) update({ tags: [...form.tags, t] });
    setTagInput("");
  };
  const removeTag = (t) => update({ tags: form.tags.filter((x) => x !== t) });

  const stripTags = (html) => (html || "").replace(/<[^>]*>/g, "");
  const hasBreak = (form.content || "").includes("data-preview-break");
  const totalChars = stripTags(form.content).length;
  const visibleChars = hasBreak
    ? stripTags((form.content || "").split(PREVIEW_BREAK_RE)[0]).length
    : 0;
  const previewPct = totalChars > 0 ? Math.min(100, Math.round((visibleChars / totalChars) * 100)) : 0;
  const removeBreak = () => update({ content: (form.content || "").replace(PREVIEW_BREAK_RE, "") });

  if (isEdit && isLoading) {
    return <div className="mx-auto max-w-7xl px-6 py-10 text-zinc-500">Chargement…</div>;
  }
  if (isEdit && !isLoading && !course) {
    return (
      <div className="mx-auto max-w-7xl px-6 py-10">
        <p className="text-zinc-500">Cours introuvable.</p>
        <Button variant="outline" className="mt-4 border-white/10" onClick={() => navigate("/admin/cours")}>
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Cours
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-6 py-10" data-testid="admin-course-editor">
      <DraftBanner draft={draft} onRestore={(d) => { setForm(d); setDirty(true); toast.success("Brouillon récupéré"); }} />
      {/* Header */}
      <div className="flex items-center justify-between gap-4 mb-8 flex-wrap">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={goBack} className="text-zinc-400 hover:text-white" data-testid="editor-back">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Cours
          </Button>
          <h1 className="font-display font-black text-2xl">
            {isEdit ? "Modifier le cours" : "Nouveau cours"}
          </h1>
        </div>
        <div className="text-xs" data-testid="editor-dirty-indicator">
          {dirty ? (
            <span className="inline-flex items-center gap-1.5 text-orange-400">
              <AlertCircle className="w-4 h-4" /> Modifications non enregistrées
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-emerald-400">
              <CheckCircle2 className="w-4 h-4" /> Tout est enregistré
            </span>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-[minmax(0,1fr)_340px] gap-8 items-start lg:min-h-[calc(100vh-6rem)]">
        {/* Colonne gauche — rédaction */}
        <div className="min-w-0 space-y-6">
          <div className="border-b border-white/10 pb-3">
            <input
              data-testid="course-form-title"
              value={form.title}
              onChange={(e) => update({ title: e.target.value })}
              placeholder="Titre du cours…"
              className="w-full bg-transparent border-0 outline-none font-display font-black text-4xl leading-tight placeholder:text-zinc-700"
            />
          </div>

          <div className="grid gap-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Description courte</Label>
            <Textarea
              data-testid="course-form-description"
              value={form.description}
              onChange={(e) => update({ description: e.target.value })}
              rows={2}
              placeholder="Résumé affiché dans les listes et aperçus."
              className="bg-white/[0.03] border-white/10"
            />
          </div>

          <div className="grid gap-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Contenu</Label>
            <RichTextEditor
              value={form.content}
              onChange={(html) => update({ content: html })}
              placeholder="## Introduction..."
              stickyTop="4rem"
              minHeight="60vh"
              enablePreviewBreak={form.is_premium}
              glossaryTerms={glossaryTerms}
              glossaryOff={form.glossary_off}
              internalTargets={internalTargets}
              leads={[{ label: "le titre", text: form.title }, { label: "la description", text: form.description }]}
              onNormalize={(html) => setForm((prev) => ({ ...prev, content: html }))}
            />
          </div>
        </div>

        {/* Colonne droite — réglages */}
        <div className="space-y-5 lg:sticky lg:top-20 lg:h-[calc(100vh-6rem)] lg:overflow-y-auto lg:overscroll-contain lg:pr-1" data-testid="editor-sidebar">
          <Card className="p-5 bg-card border-white/5 space-y-4">
            <div className="text-xs uppercase tracking-[0.2em] text-emerald-400">Publication</div>
            <div className="flex items-center gap-3">
              <Switch
                data-testid="course-form-published"
                checked={form.published}
                onCheckedChange={(v) => update({ published: v })}
              />
              <Label className="cursor-pointer text-sm">{form.published ? "Publié" : "Brouillon"}</Label>
            </div>
            <AuthorPicker
              value={form.author_ids || []}
              onChange={(ids) => update({ author_ids: ids })}
              currentUserId={user?.id}
              testid="course-form-author"
            />
            <div className="flex items-center gap-2">
              <Button
                onClick={save}
                disabled={saving}
                className="flex-1 bg-emerald-500 text-black hover:bg-emerald-300"
                data-testid="course-form-submit"
              >
                <Save className="w-4 h-4 mr-1.5" />
                {saving ? "Enregistrement…" : "Enregistrer"}
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="border-white/10 shrink-0"
                onClick={viewCourse}
                disabled={!slugForView}
                title="Voir le cours"
                data-testid="editor-view-course"
              >
                <Eye className="w-4 h-4" />
              </Button>
            </div>
            <DraftStatus draft={draft} />
          </Card>
          <Card className="p-5 bg-card border-white/5 space-y-3" data-testid="course-form-toc">
            <div className="text-xs uppercase tracking-[0.2em] text-emerald-400">Sommaire</div>
            <TocPanel html={form.content} toc={form.toc} onChange={(toc) => update({ toc })} />
          </Card>

          {/* Image : uniquement pour les rétrospectives (affichée en haut du cours) */}
          {form.category === "article" && (
            <Card className="p-5 bg-card border-white/5 space-y-3">
              <div className="text-xs uppercase tracking-[0.2em] text-emerald-400">Image de la rétrospective</div>
              <CoverImageDropzone
                value={form.thumbnail}
                onChange={(url) => update({ thumbnail: url })}
                testid="course-form-cover"
              />
              <p className="text-[11px] text-zinc-500">Facultative. Affichée en haut du cours et dans l'aperçu quand le lien est partagé.</p>
            </Card>
          )}

          <Card className="p-5 bg-card border-white/5 space-y-4">
            <div className="text-xs uppercase tracking-[0.2em] text-emerald-400">Classement</div>
            <div className="grid gap-2">
              <Label className="text-xs text-zinc-500">Type de contenu</Label>
              <Toggle2
                options={CONTENT_TYPES}
                value={form.category}
                onChange={(v) => update({ category: v })}
                testid="course-form-category"
              />
            </div>
            {form.category === "concept" && (() => {
              const t = themes.find((x) => x.id === form.theme_id);
              const pos = t && id ? t.course_ids.indexOf(id) : -1;
              return (
                <div className="grid gap-2" data-testid="course-form-theme">
                  <Label className="text-xs text-zinc-500">Thématique</Label>
                  <select
                    value={form.theme_id}
                    onChange={(e) => update({ theme_id: e.target.value })}
                    className="h-10 rounded-md bg-white/[0.03] border border-white/10 px-2 text-sm"
                  >
                    <option value="">Aucune (affiché dans « Autres concepts »)</option>
                    {themes.map((x) => <option key={x.id} value={x.id}>{x.title}</option>)}
                  </select>
                  <div className="flex items-center justify-between text-xs text-zinc-500">
                    <span>
                      {t ? (pos >= 0 ? <>Position : <b className="text-zinc-200">{pos + 1}</b> sur {t.course_ids.length}</> : "Sera ajouté à la fin de la thématique") : "Pas encore rangé"}
                    </span>
                    {t && <Link to="/admin/cours/thematiques" className="text-emerald-400 hover:text-emerald-300">Modifier l'ordre →</Link>}
                  </div>
                </div>
              );
            })()}
            {form.category === "article" && (
              <RetroDateFields start={form.year_start} end={form.year_end} onChange={(v) => update(v)} />
            )}
            <div className="grid gap-2">
              <Label className="text-xs text-zinc-500">Niveau</Label>
              <Toggle2
                options={LEVEL_OPTIONS}
                value={form.level}
                onChange={(v) => update({ level: v })}
                testid="course-form-level"
              />
              {!form.level && (
                <p className="text-[11px] text-orange-400/90">Aucun niveau choisi : le cours s'affichera sans badge de niveau.</p>
              )}
            </div>
            <div className="grid gap-2">
              <Label className="text-xs text-zinc-500">Tags</Label>
              <div className="flex flex-wrap gap-1.5">
                {form.tags.map((t) => (
                  <span key={t} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/5 border border-white/10 text-xs text-zinc-300" data-testid={`course-tag-${t}`}>
                    {t}
                    <button type="button" onClick={() => removeTag(t)} className="text-zinc-500 hover:text-rose-300" aria-label={`Retirer ${t}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              <Input
                data-testid="course-form-tags"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); addTag(); }
                }}
                placeholder="Taper un mot puis Entrée…"
                className="bg-white/[0.03] border-white/10"
              />
            </div>
            <div className="grid gap-2">
              <Label className="text-xs text-zinc-500">Vidéo YouTube (optionnel)</Label>
              <Input
                data-testid="course-form-youtube"
                value={form.youtube_url || ""}
                onChange={(e) => update({ youtube_url: e.target.value })}
                placeholder="https://youtube.com/…"
                className="bg-white/[0.03] border-white/10"
              />
            </div>
          </Card>

          <Card className="p-5 bg-card border-white/5 space-y-3">
            <div className="text-xs uppercase tracking-[0.2em] text-emerald-400">Contenus liés</div>
            <p className="text-xs text-zinc-500 leading-relaxed">
              Suggérés en bas de ce cours. Un lien réciproque s'affiche aussi sur l'autre contenu.
            </p>
            <CourseLinksPicker
              value={form.links}
              onChange={(links) => update({ links })}
              courseId={id}
            />
          </Card>

          <GlossaryPanel
            html={form.content}
            terms={glossaryTerms}
            off={form.glossary_off}
            onChange={(off) => update({ glossary_off: off })}
          />

          <Card className="p-5 bg-card border-white/5 space-y-4">
            <div className="text-xs uppercase tracking-[0.2em] text-emerald-400">Accès & aperçu</div>
            <div className="grid gap-2">
              <Label className="text-xs text-zinc-500">Palier requis</Label>
              <Toggle2
                options={[{ id: "free", label: "Gratuit" }, { id: "premium", label: "Essentiel+" }]}
                value={form.is_premium ? "premium" : "free"}
                onChange={(v) => update({ is_premium: v === "premium" })}
                testid="course-form-premium-switch"
              />
            </div>

            {!form.is_premium ? (
              <p className="text-xs text-zinc-400 leading-relaxed" data-testid="preview-free-note">
                Cours gratuit : il est visible en entier par tout le monde.
              </p>
            ) : (
              <>
                {hasBreak ? (
                  <div className="rounded-lg border border-violet-500/40 bg-violet-500/10 p-3 space-y-2.5" data-testid="preview-break-status">
                    <p className="text-xs text-violet-200 leading-relaxed">
                      ✓ Coupure placée dans le texte — les non-abonnés voient tout ce qui est au-dessus du trait violet.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-violet-500/40 text-violet-200 hover:bg-violet-500/20"
                      onClick={removeBreak}
                      data-testid="remove-preview-break"
                    >
                      Retirer la coupure
                    </Button>
                  </div>
                ) : (
                  <>
                    <p className="text-xs text-zinc-400 leading-relaxed">
                      Placez le curseur dans le texte à l'endroit voulu, puis cliquez sur « ✂ Fin d'aperçu » dans la barre d'outils.
                    </p>
                    <p className="text-xs text-violet-300/90 leading-relaxed">
                      Sans coupure, les non-abonnés ne verront que le titre et la description.
                    </p>
                  </>
                )}

                <div className="space-y-1.5" data-testid="preview-progress">
                  <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full bg-violet-500 transition-all" style={{ width: `${previewPct}%` }} />
                  </div>
                  <div className="text-[11px] text-zinc-500">
                    Visible gratuitement : {visibleChars} / {totalChars} car. · {previewPct} %
                  </div>
                </div>
              </>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
